import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from 'Components/UI/Button';
import RichTextEditor from 'Components/UI/RichTextEditor';
import MeetingTemplateDialog from 'Components/UI/TemplateDialog';
import FileChips from 'Components/UI/FileChips';
import EmailTypePicker from 'Components/Modals/EmailTypePicker';
import TemplateOptionsMenu from 'Components/UI/TemplateOptionsMenu';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import useUserContext from 'Context/UserContext';
import { pick } from 'Helpers/Affordances';
import { useTemplatesStateContext } from 'Context/TemplateContext';
import modalConsts from 'Consts/ModalNames';
import { HistoryOutlined } from '@material-ui/icons';
import ScheduleSendDatePicker from 'Components/Modals/ScheduleSendDatePicker';
import { openModal } from 'Helpers/ModalHelper';
import { useSnackbarContext } from 'Context/SnackbarContext';
import ScheduleButtonPicker from 'Components/UI/EmailForm/ScheduleButtonPicker';
import fr from 'facet-rest';
import RestoreGeneratedDraftOption
  from 'Components/UI/EmailForm/RestoreGeneratedDraftOption/RestoreGeneratedDraftOption';
import ConfirmationDialog from '../ConfirmationDialog';
import { createInputBaseStyles } from '../styles';
import TextField from '../TextField';
import EmailAttachmentForm from '../EmailAttachmentForm';
import RichTextEditorCustomContextMenu from '../RichTextEditorCustomContextMenu';
import { TEMPLATE_TYPES } from '../TemplateOptionsMenu/utils';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  subjectLine: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
  },
  subjectInputBase: {
    ...createInputBaseStyles(theme),
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    paddingRight: theme.spacing(1),
    paddingBottom: 0,
    '& input': {
      padding: theme.spacing(1),
    },
    '& label': {
      transform: 'translate(14px, 10px) scale(1)',
    },
  },
  emailInput: {
    marginTop: theme.spacing(1),
    width: theme.dimensions.lg,
    height: '100%',
  },
  updatedEmailInput: {
    width: '100%',
  },
  attachments: {
    marginTop: theme.spacing(1),
    width: theme.dimensions.lg,
  },
  actionRow: {
    '& > *': {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
      flexShrink: 0,
    },
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
    display: 'flex',
  },
  loadingBar: {
    display: 'flex',
    width: '100%',
    marginTop: theme.spacing(1),
  },
  loadingIndicator: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
  },
  loadingLabel: {
    color: theme.palette.facet.primaryGray,
  },
  attachmentsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: theme.dimensions.lg,
    marginTop: theme.spacing(2),
  },
  attachmentButton: {
    marginRight: theme.spacing(1),
  },
  saveButton: {
    marginRight: theme.spacing(1),
  },
  mcTemplateOptionsMenu: {
    borderLeft: `1px solid ${theme.palette.facet.lightGray2}`,
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
});

function EmailForm({
  body,
  className,
  classes,
  selectedTemplate,
  onChangeBody,
  onChangeSubject,
  onRestoreGeneratedContent,
  onSave,
  onSaveMeetingTemplate,
  subject,
  attachments,
  meeting,
  onAttachmentChange,
  saved,
  isValidTemplateName,
  onSendClientEmail,
  agenda,
  updateMeetingTemplates,
  onScheduleSendEmail,
  showPreview,
  onScheduledDateChange,
  showSaveButton,
  confirmButtonText,
  sendTypeOverride,
  onInsertTemplate,
  EditorProps,
}) {
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = React.useState(false);
  const [showDeleteTemplateDialog, setShowDeleteTemplateDialog] = React.useState(false);
  const { agentResource } = useUserContext();
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [emailTypePickerIsOpen, setEmailTypePickerVisibility] = React.useState(false);
  const [scheduledSendDatePickerIsOpen, setScheduledSendDatePickerIsOpen] = React.useState(false);
  const [sendType, setSendType] = React.useState('live');
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const onStartFileUpload = () => setLoading(true);
  const onFinishFileUpload = () => setLoading(false);

  const handleDeleteMeetingTemplate = async () => {
    if (selectedTemplate) {
      try {
        await selectedTemplate.delete();
        updateMeetingTemplates();
        displaySuccess('Template was successfully deleted.');
      } catch (e) {
        displayErrorMessage(e, e.errors);
      }
    }
  };

  const EmailEditor = pick(agentResource, {
    [fr.relationships.notesContextMenu.name]: RichTextEditorCustomContextMenu,
  }, RichTextEditor);

  const emailProps = pick(agentResource, {
    [fr.relationships.notesContextMenu.name]: {
      className: classNames(classes.emailInput, classes.updatedEmailInput),
      onChange: v => onChangeBody(v),
      value: body,
      disableTextColors: true,
      ...EditorProps,
    },
  }, {
    className: classNames(classes.emailInput, classes.updatedEmailInput),
    onChange: v => onChangeBody(v),
    value: body,
    disableTextColors: true,
    ...EditorProps,
  });

  const handleOnClickScheduleSend = () => {
    setSendType('scheduled');
    if (agenda.attr('scheduledSendType')) {
      setEmailTypePickerVisibility(true);
    } else {
      setScheduledSendDatePickerIsOpen(true);
    }
  };

  const handleOnClickSendClientEmail = () => {
    setSendType(sendTypeOverride || 'live');
    setEmailTypePickerVisibility(true);
  };

  // Templates
  const {
    templates,
  } = useTemplatesStateContext();

  return (
    <div className={classNames(classes.root, className)}>
      <EmailTypePicker
        isOpen={emailTypePickerIsOpen}
        closeModal={() => setEmailTypePickerVisibility(false)}
        openModal={() => setEmailTypePickerVisibility(true)}
        onSubmit={onSendClientEmail}
        onScheduleSendEmail={onScheduleSendEmail}
        meetingResource={meeting}
        sendType={sendType}
        meetingAgendaResource={agenda}
      />

      <ScheduleSendDatePicker
        isOpen={scheduledSendDatePickerIsOpen}
        closeModal={() => setScheduledSendDatePickerIsOpen(false)}
        meetingAgendaResource={agenda}
        onSubmit={(meetingAgendaResource) => {
          onScheduledDateChange(meetingAgendaResource);
          handleOnClickScheduleSend();
        }}
      />

      <div className={classes.subjectLine}>
        <TextField
          classes={{
            root: classes.subjectInputBase,
          }}
          label="Subject"
          fullWidth
          variant="outlined"
          onChange={evt => onChangeSubject(evt.target.value)}
          value={subject}
          test="subject-input-field"
        />

        <IconButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openModal(modalConsts.historyFlyout, [
              { title: btoa('Meeting Agenda History') },
              { link: btoa(agenda.relationship('history').link('related').url()) },
            ]);
          }}
        >
          <HistoryOutlined />
        </IconButton>
      </div>
      <EmailEditor {...emailProps} />
      {attachments && (
        <div className={classes.attachmentsRow}>
          <FileChips
            fileCollection={attachments}
            onDelete={onAttachmentChange}
          />
        </div>
      )}
      {loading && (
        <div className={classes.loadingIndicator}>
          <LinearProgress
            color="secondary"
            variant="determinate"
            value={progress}
            className={classes.loadingBar}
          />
          <Typography variant="h6" className={classes.loadingLabel}>
            {progress === 100 ? 'Processing...' : `${progress}%`}
          </Typography>
        </div>
      )}

      {/* ---------------- EMAIL FORM ACTIONS ---------------- */}
      <div className={classes.actionRow}>
        <EmailAttachmentForm
          className={classes.attachmentButton}
          agenda={agenda}
          householdId={meeting.attr('householdId')}
          onAttachmentChange={onAttachmentChange}
          onFinishFileUpload={onFinishFileUpload}
          onStartFileUpload={onStartFileUpload}
          setUploadProgress={setProgress}
        />
        {showSaveButton && (
          <Button
            className={classNames(classes.saveButton)}
            color="secondary"
            disableElevation
            onClick={onSave}
            variant="contained"
            disabled={saved}
          >
            {saved ? 'Draft Saved' : 'Save Draft'}
          </Button>
        )}
        {showPreview && (
          <Button
            color="primary"
            disableElevation
            onClick={async () => {
              await onSave();
              openModal(modalConsts.clientMeetingPreview, [
                { meetingId: meeting.id() },
              ]);
            }}
            variant="contained"
          >
            {'Preview Client Meeting'}
          </Button>
        )}
        <ScheduleButtonPicker
          agentResource={agentResource}
          agendaResource={agenda}
          saved={saved}
          onClickSend={handleOnClickSendClientEmail}
          sendButtonLabel={confirmButtonText}
          onClickSchedule={() => {
            setScheduledSendDatePickerIsOpen(true);
          }}
        />

        <RestoreGeneratedDraftOption
          agentResource={agentResource}
          meetingAgendaResource={agenda}
          handleChange={onRestoreGeneratedContent}
        />

        <TemplateOptionsMenu
          useLegacyButton
          className={classes.mcTemplateOptionsMenu}
          templates={templates}
          onInsertTemplate={template => {
            onInsertTemplate(template);
          }}
          templateDraftContent={body}
          templateSubject={subject}
          templateType={TEMPLATE_TYPES.messageMeetingCombo}
          confirmBeforeInsert
        />
      </div>

      {showDeleteTemplateDialog && (
        <ConfirmationDialog
          onCancel={() => setShowDeleteTemplateDialog(false)}
          title="Are you sure you want to delete this meeting template?"
          message="The current text will not be replaced."
          onConfirm={() => {
            setShowDeleteTemplateDialog(false);
            handleDeleteMeetingTemplate();
          }}
          showCloseIcon={false}
        />
      )}

      {showSaveTemplateDialog && (
        <MeetingTemplateDialog
          template={selectedTemplate}
          onSave={(template) => {
            onSaveMeetingTemplate(template);
            setShowSaveTemplateDialog(false);
          }}
          onCancel={() => {
            setShowSaveTemplateDialog(false);
          }}
          saveLabel="Save template"
          isValidTemplateName={isValidTemplateName}
        />
      )}

    </div>
  );
}

EmailForm.defaultProps = {
  className: '',
  templateOptions: null,
  templateOptionsPrompt: null,
  selectedTemplate: null,
  selectedTemplateId: null,
  onChangeTemplate: () => { },
  attachments: null,
  onAttachmentChange: () => { },
  onScheduledDateChange: () => { },
  saved: false,
  showSaveEmailTemplate: false,
  isValidTemplateName: () => true,
  showPreview: true,
  showSaveButton: true,
  confirmButtonText: 'Send to Client',
  sendTypeOverride: null,
  onInsertTemplate: () => { },
  EditorProps: {},
};

EmailForm.propTypes = {
  body: PropTypes.string.isRequired,
  meeting: PropTypes.object.isRequired,
  agenda: PropTypes.object.isRequired,
  className: PropTypes.string,
  templateOptions: PropTypes.array,
  templateOptionsPrompt: PropTypes.string,
  selectedTemplate: PropTypes.object,
  selectedTemplateId: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveMeetingTemplate: PropTypes.func.isRequired,
  onChangeBody: PropTypes.func.isRequired,
  onChangeSubject: PropTypes.func.isRequired,
  onChangeTemplate: PropTypes.func,
  subject: PropTypes.string.isRequired,
  attachments: PropTypes.object,
  onAttachmentChange: PropTypes.func,
  isValidTemplateName: PropTypes.func,
  saved: PropTypes.bool,
  showSaveEmailTemplate: PropTypes.bool,
  onSendClientEmail: PropTypes.func.isRequired,
  onScheduleSendEmail: PropTypes.func.isRequired,
  updateMeetingTemplates: PropTypes.func.isRequired,
  showPreview: PropTypes.bool,
  showSaveButton: PropTypes.bool,
  onScheduledDateChange: PropTypes.func,
  confirmButtonText: PropTypes.string,
  sendTypeOverride: PropTypes.string,
  onInsertTemplate: PropTypes.func,
  EditorProps: PropTypes.object,
};

export default withStyles(styles)(EmailForm);
